import { useCallback, useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { GoogleSignInButton } from '@/features/admin/components/GoogleSignInButton';
import { ApiError } from '@/lib/api-client';
import {
  useAdminAuthConfig,
  useAdminBreakglassStart,
  useAdminBreakglassVerify,
  useAdminDevSignIn,
  useAdminGoogleSignIn,
  useAdminMe,
} from '@/features/admin/hooks';
import {
  AdminAuthAlert,
  AdminAuthButton,
  AdminAuthField,
  AdminAuthInput,
  AdminAuthLink,
  AdminAuthShell,
  type AdminAuthAlertState,
} from '@/features/admin/components/AdminAuthShell';

/**
 * Admin login route. Uses the admin sign-in chrome (AdminAuthShell), which
 * matches the regara-ops login screen: deep-navy page, one frosted card with
 * the logo, "Admin Console" and the sign-in control. The page is chrome-less
 * (no navbar / subnav) — admin operators reach it directly by URL.
 *
 * The sign-in method offered is driven entirely by the backend's
 * /api/admin/auth/config: Google Workspace sign-in in prod/staging, a
 * bare email field in local dev, and an optional password + emailed-code
 * emergency path when break-glass is enabled.
 */

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Could not sign in. Please try again.';
}

/**
 * A 401 on a sign-in call means "not an admin" / "wrong credentials" — but
 * `lib/api-client.ts` deliberately discards the server's actual `detail` text
 * for every 401 (even with `skipAuthRedirect`, it throws a bare
 * `new ApiError('Unauthorized', 401)`), so the exact backend copy can't be read
 * off `err.message` here. Hardcoded per the documented backend contract
 * instead. Anything else (503, network, etc.) falls back to the real message.
 */
function notAdminMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 401) {
    return 'Only @regara.ai accounts can sign in.';
  }
  return errorMessage(err);
}

function invalidCodeMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 401) {
    return 'That code is invalid or has expired.';
  }
  return errorMessage(err);
}

const devSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email.'),
});
type DevForm = z.infer<typeof devSchema>;

function DevSignInForm({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (message: string) => void;
}) {
  const dev = useAdminDevSignIn();
  const form = useForm<DevForm>({
    resolver: zodResolver(devSchema),
    defaultValues: { email: '' },
    mode: 'onSubmit',
  });

  const onSubmit = async (values: DevForm) => {
    try {
      await dev.mutateAsync(values.email);
      onSuccess();
    } catch (err) {
      onError(notAdminMessage(err));
    }
  };

  return (
    <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
      <AdminAuthField
        label="Email Address"
        htmlFor="admin-dev-email"
        error={form.formState.errors.email?.message}
      >
        <AdminAuthInput
          id="admin-dev-email"
          type="email"
          autoComplete="username"
          placeholder="admin@regara.ai"
          {...form.register('email')}
        />
      </AdminAuthField>
      <AdminAuthButton type="submit" disabled={dev.isPending}>
        {dev.isPending ? 'Signing in…' : 'Sign In'}
      </AdminAuthButton>
    </form>
  );
}

const breakglassStartSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email.'),
  password: z.string().min(1, 'Password is required'),
});
type BreakglassStartForm = z.infer<typeof breakglassStartSchema>;

const breakglassCodeSchema = z.object({
  code: z.string().min(1, 'Code is required'),
});
type BreakglassCodeForm = z.infer<typeof breakglassCodeSchema>;

function BreakglassForm({ onSuccess }: { onSuccess: () => void }) {
  const start = useAdminBreakglassStart();
  const verify = useAdminBreakglassVerify();
  const [step, setStep] = useState<'password' | 'code'>('password');
  const [email, setEmail] = useState('');
  const [info, setInfo] = useState<AdminAuthAlertState>(null);
  const [error, setError] = useState<AdminAuthAlertState>(null);

  const startForm = useForm<BreakglassStartForm>({
    resolver: zodResolver(breakglassStartSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
  });
  const codeForm = useForm<BreakglassCodeForm>({
    resolver: zodResolver(breakglassCodeSchema),
    defaultValues: { code: '' },
    mode: 'onSubmit',
  });

  const onStart = async (values: BreakglassStartForm) => {
    setError(null);
    try {
      const res = await start.mutateAsync(values);
      setEmail(values.email);
      setInfo({ kind: 'info', text: res.message });
      setStep('code');
    } catch (err) {
      setError({ kind: 'error', text: errorMessage(err) });
    }
  };

  const onVerify = async (values: BreakglassCodeForm) => {
    setError(null);
    try {
      await verify.mutateAsync({ email, code: values.code });
      onSuccess();
    } catch (err) {
      setError({ kind: 'error', text: invalidCodeMessage(err) });
    }
  };

  return (
    <div>
      {step === 'password' ? (
        <form noValidate onSubmit={startForm.handleSubmit(onStart)}>
          <AdminAuthField
            label="Email Address"
            htmlFor="admin-bg-email"
            error={startForm.formState.errors.email?.message}
          >
            <AdminAuthInput
              id="admin-bg-email"
              type="email"
              autoComplete="username"
              placeholder="admin@regara.ai"
              {...startForm.register('email')}
            />
          </AdminAuthField>
          <AdminAuthField
            label="Emergency password"
            htmlFor="admin-bg-password"
            error={startForm.formState.errors.password?.message}
          >
            <AdminAuthInput
              id="admin-bg-password"
              type="password"
              autoComplete="current-password"
              {...startForm.register('password')}
            />
          </AdminAuthField>
          <AdminAuthButton type="submit" disabled={start.isPending}>
            {start.isPending ? 'Sending…' : 'Send code'}
          </AdminAuthButton>
        </form>
      ) : (
        <form noValidate onSubmit={codeForm.handleSubmit(onVerify)}>
          <AdminAuthField
            label="Code"
            htmlFor="admin-bg-code"
            error={codeForm.formState.errors.code?.message}
          >
            <AdminAuthInput
              id="admin-bg-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              {...codeForm.register('code')}
            />
          </AdminAuthField>
          <AdminAuthButton type="submit" disabled={verify.isPending}>
            {verify.isPending ? 'Verifying…' : 'Verify'}
          </AdminAuthButton>
        </form>
      )}
      <AdminAuthAlert alert={info} />
      <AdminAuthAlert alert={error} />
    </div>
  );
}

export function AdminLoginPage() {
  const me = useAdminMe();
  const config = useAdminAuthConfig();
  const [alert, setAlert] = useState<AdminAuthAlertState>(null);
  const [emergency, setEmergency] = useState(false);
  const google = useAdminGoogleSignIn();

  // Already-authenticated admin → bounce straight to the Users page (the primary
  // admin task: inviting users + setting their AI data-use track).
  useEffect(() => {
    if (me.data) {
      window.location.replace('/admin/dashboard');
    }
  }, [me.data]);

  const onGoogle = useCallback(
    async (credential: string) => {
      setAlert(null);
      try {
        await google.mutateAsync(credential);
        window.location.assign('/admin/dashboard');
      } catch (e) {
        setAlert({ kind: 'error', text: notAdminMessage(e) });
      }
    },
    [google],
  );

  // Memoized so GoogleSignInButton's effect (initialize + renderButton) only
  // re-runs when the client id actually changes, not on every AdminLoginPage
  // render — an inline arrow here would otherwise be a fresh function every
  // time and re-trigger that effect, stacking duplicate Google buttons.
  const onSignInError = useCallback((m: string) => {
    setAlert({ kind: 'error', text: m });
  }, []);

  const onDevSuccess = useCallback(() => {
    window.location.assign('/admin/dashboard');
  }, []);

  const onBreakglassSuccess = useCallback(() => {
    window.location.assign('/admin/dashboard');
  }, []);

  if (config.isLoading) {
    return (
      <AdminAuthShell>
        <p className="text-sm text-white/60">Loading…</p>
      </AdminAuthShell>
    );
  }
  if (!config.data) {
    return (
      <AdminAuthShell>
        <AdminAuthAlert alert={{ kind: 'error', text: 'Admin sign-in is unavailable.' }} />
      </AdminAuthShell>
    );
  }

  return (
    <AdminAuthShell>
      {!emergency && config.data.mode === 'google' && (
        <GoogleSignInButton
          clientId={config.data.google_client_id ?? ''}
          onCredential={onGoogle}
          onError={onSignInError}
        />
      )}

      {!emergency && config.data.mode === 'dev' && (
        <DevSignInForm onSuccess={onDevSuccess} onError={onSignInError} />
      )}

      {!emergency && <AdminAuthAlert alert={alert} />}

      {config.data.breakglass_enabled && (
        <div className="mt-6">
          {!emergency ? (
            <AdminAuthLink onClick={() => setEmergency(true)}>Emergency sign-in</AdminAuthLink>
          ) : (
            <BreakglassForm onSuccess={onBreakglassSuccess} />
          )}
        </div>
      )}
    </AdminAuthShell>
  );
}

export const Route = createFileRoute('/admin/login')({
  component: AdminLoginPage,
});
